import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../api/portalApi';
import { useRuntimeConfig } from './RuntimeConfigContext';
import type { Project } from '../types';

const STORAGE_KEY = 'fint_active_project_id';
const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

export interface ProjectCtx {
  projects: Project[];
  activeProjectId: string | null;
  activeProject: Project | null;
  loading: boolean;
  setActiveProjectId: (id: string | null) => void;
  refreshProjects: () => Promise<void>;
}

const ProjectContext = createContext<ProjectCtx>({
  projects: [],
  activeProjectId: null,
  activeProject: null,
  loading: true,
  setActiveProjectId: () => {},
  refreshProjects: async () => {},
});

export function useProject() {
  return useContext(ProjectContext);
}

interface Props {
  children: React.ReactNode;
}

// Tracks the org's Projects (product/project context profiles) and which one is
// "active" for AI generation — mirrors UserRoleContext's provider pattern, but does
// its own data fetching (no gate states needed; an empty/failed list just means the
// app runs in fallback/no-context mode, per the Context Composer's design).
export function ProjectProvider({ children }: Props) {
  const { instance } = useMsal();
  const cfg = useRuntimeConfig();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY),
  );

  const refreshProjects = useCallback(async () => {
    if (DEV_BYPASS) { setLoading(false); return; }
    const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
    try {
      setProjects(await api.getProjects());
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, cfg.msalClientId, cfg.apiBaseUrl]);

  useEffect(() => { refreshProjects(); }, [refreshProjects]);

  const setActiveProjectId = useCallback((id: string | null) => {
    setActiveProjectIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Drop a stale selection once the list has loaded (project renamed away/deleted
  // elsewhere) so the UI doesn't keep pointing at an id that no longer resolves.
  useEffect(() => {
    if (loading || !activeProjectId) return;
    if (!projects.some(p => p.id === activeProjectId)) setActiveProjectId(null);
  }, [loading, projects, activeProjectId, setActiveProjectId]);

  const activeProject = useMemo(
    () => projects.find(p => p.id === activeProjectId) ?? null,
    [projects, activeProjectId],
  );

  const value = useMemo<ProjectCtx>(() => ({
    projects, activeProjectId, activeProject, loading, setActiveProjectId, refreshProjects,
  }), [projects, activeProjectId, activeProject, loading, setActiveProjectId, refreshProjects]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export default ProjectContext;
